import { nth, sortBy } from 'lodash';
import React, { useCallback, useMemo, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { Button, Form, FormGroup, Label } from 'reactstrap';
import { gql, useQuery } from 'urql';
import { CenteredContainer, Loading } from '.';
import { useId } from '../hooks';
import { GraphDeepPartialNotListed, GraphDeepPartialNotListedAndOverride, GraphDeepPartialOverride } from '../types';
import { DangerousGoods, HacpackQuery, HacpackQueryTerminalArguments, Handling, HandlingAttachment, HarbourOfficeAuthorization, PortAuthorityAuthorization, Terminal, TerminalHandlingsArguments } from '../types/graph';
import HandlingDetailPage from './HandlingDetail';
import { GridLayout } from './ui';

// @ts-ignore
type TerminalUnitSearchQueryData = GraphDeepPartialOverride<HacpackQuery, {
    terminal: TerminalUnitSearchTerminal
}>

// @ts-ignore
type TerminalUnitSearchTerminal = GraphDeepPartialNotListedAndOverride<Terminal, 'id', {
    handlings: TerminalUnitSearchTerminalHandling[]
}>

// @ts-ignore
type TerminalUnitSearchTerminalHandling = GraphDeepPartialNotListedAndOverride<Handling, 'id' | 'creationDate' | 'loadingUnitIdentifier' | 'checkedIn' | 'checkedOut' | 'checkInDate' | 'checkOutDate' | 'shippingAgencyOrCarrier' | 'loadingUnitSize' | 'loadingUnitSizeFromGate' | 'type' | 'ship' | 'boardingShip' | 'unboardingShip' | 'voyage' | 'area' | 'bay' | 'row' | 'tier' | 'equipmentType' | 'direct' | 'approvedByTerminal' | 'checkedByTerminal' | 'prescriptions' | 'boardingPrescriptions' | 'unboardingPrescriptions' | 'booking' | 'bookingFromTerminal' | 'boardingShipFromTerminal' | 'unboardingShipFromTerminal' | 'qsaNotes' | 'fei' | 'cei' | 'standardDistance' | 'originVoyage' | 'destinationVoyage', {
    harbourOfficeAuthorization: TerminalUnitSearchTerminalHandlingsHarbourOfficeAuthorization,
    portAuthorityAuthorization: TerminalUnitSearchTerminalPortAuthorityAuthorization,
    dangerousGoods: TerminalUnitSearchTerminalHandlingsDangerousGoods[],
    chemistDocuments: TerminalUnitSearchTerminalHandlingsChemistDocument[]
}>

// @ts-ignore
type TerminalUnitSearchTerminalHandlingsHarbourOfficeAuthorization = GraphDeepPartialNotListed<HarbourOfficeAuthorization, 'authorization'>
// @ts-ignore
type TerminalUnitSearchTerminalPortAuthorityAuthorization = GraphDeepPartialNotListed<PortAuthorityAuthorization, 'authorized' | 'fileNumber' | 'downloadUrl'>
// @ts-ignore
type TerminalUnitSearchTerminalHandlingsDangerousGoods = GraphDeepPartialNotListedAndOverride<DangerousGoods, 'id' | 'unNumber' | 'adrClass' | 'adrSecondaryClasses' | 'packingGroup' | 'weight' | 'numberOfPackages' | 'packaging' | 'flashPoint' | 'limitedQuantity', {
    safetyDataSheet: TerminalUnitSearchTerminalHandlingsDangerousGoodsSafetyDataSheet
}>
// @ts-ignore
type TerminalUnitSearchTerminalHandlingsDangerousGoodsSafetyDataSheet = GraphDeepPartialNotListed<HandlingAttachment, 'id' | 'downloadUrl'>
// @ts-ignore
type TerminalUnitSearchTerminalHandlingsChemistDocument = GraphDeepPartialNotListed<HandlingAttachment, 'id' | 'documentTitle' | 'downloadUrl'>

type TerminalUnitSearchQueryArguments = HacpackQueryTerminalArguments & TerminalHandlingsArguments;

const terminalUnitSearchQuery = gql<TerminalUnitSearchQueryData, TerminalUnitSearchQueryArguments>`
query terminal($id: Int!, $loadingUnitIdentifiers: [String!], $checkedOut: Boolean) {
    terminal(id:$id) {
        id
        name
        port
        feiLimit
        ceiLimit
        radiusLimit
        areas {
            id
            name
            description
        }
        handlings(loadingUnitIdentifiers: $loadingUnitIdentifiers, checkedOut: $checkedOut) {
            id
            creationDate
            loadingUnitIdentifier
            checkedIn
            checkedOut
            checkInDate
            checkOutDate
            shippingAgencyOrCarrier
            loadingUnitSize
            loadingUnitSizeFromGate
            type
            ship
            boardingShip
            unboardingShip
            voyage
            originVoyage
            destinationVoyage
            area
            bay
            row
            tier
            equipmentType
            direct
            approvedByTerminal
            checkedByTerminal
            prescriptions
            boardingPrescriptions
            unboardingPrescriptions
            booking
            bookingFromTerminal
            boardingShipFromTerminal
            unboardingShipFromTerminal
            qsaNotes
            fei
            cei
            standardDistance
            harbourOfficeAuthorization {
                authorization
            }
            portAuthorityAuthorization {
                authorized
                fileNumber
                downloadUrl
            }
            dangerousGoods {
                id
                unNumber
                packingGroup
                packaging
                weight
                numberOfPackages
                provision274
                flashPoint
                adrClass
                adrSecondaryClasses
                marinePollutant
                limitedQuantity
                fumigated
                coolingAgents
                elevatedTemperature
                emergencyCardUrl
                safetyDataSheet {
                    id
                    downloadUrl
                }
            }
            chemistDocuments {
                id
                documentTitle
                downloadUrl
            }
        }
    }
}
`;

type TerminalUnitSearchProps = {
    id: number
}

const TerminalUnitSearch = ({ id }: TerminalUnitSearchProps) => {
    const [loadingUnitIdentifierSearch, setLoadingUnitIdentifierSearch] = useState<string | null>(null);

    //const { isSuperuser } = useCurrentUser();

    const [result] = useQuery<TerminalUnitSearchQueryData, TerminalUnitSearchQueryArguments>({
        query: terminalUnitSearchQuery,
        variables: { id, loadingUnitIdentifiers: [loadingUnitIdentifierSearch], checkedOut: null },
        pause: loadingUnitIdentifierSearch === null
    });

    const { data, fetching } = result;

    const { t } = useTranslation('common');

    const { id: formId } = useId(5);

    const { register, handleSubmit } = useForm();
    //const { isSubmitted, errors } = formState;

    const onSubmit = useCallback(({ loadingUnitNumberSearch }: FieldValues) => {
        setLoadingUnitIdentifierSearch(loadingUnitNumberSearch);
    }, [setLoadingUnitIdentifierSearch]);

    const header = useMemo(() => <Form id={`terminalUnitSearch-${formId}`} onSubmit={handleSubmit(onSubmit)} noValidate inline className={'p-2'}>
        <FormGroup className="mb-2 mr-sm-2 mb-sm-0">
            <Label htmlFor={`loadingUnitIdenatifierSearch-${formId}`} className="mr-sm-2">{t('Sigla')}</Label>
            <input id={`loadingUnitIdenatifierSearch-${formId}`} className={'form-control'} type="search" {...register('loadingUnitNumberSearch', { required: true, minLength: 6, maxLength: 15, pattern: /^[a-zA-Z0-9]+$/ })} />
        </FormGroup>

        <Button disabled={fetching} type="submit">{t('Cerca')}</Button>
    </Form>, [t, formId, handleSubmit, onSubmit, register, fetching]);

    const terminal = data?.terminal;
    const unitFound = useMemo(() => {
        const handlings = data?.terminal?.handlings ?? [];

        if (handlings.length === 0) {
            return null;
        }

        const now = Date.now();

        const unit = nth(sortBy(data?.terminal?.handlings, (handling) => {
            if (handling.checkedIn === true && handling.checkedOut === false) {
                return 0;
            }
            if (handling.creationDate === undefined) {
                return Infinity;
            }
            return Math.abs(now - Date.parse(handling.creationDate));
        }), 0);
        return unit ?? null;
    }, [data]);

    const unit = useMemo(() => {
        if (terminal === undefined ||  unitFound === null) {
            return <CenteredContainer>{t('Nessuna unità trovata.')}</CenteredContainer >;
        }

        return <HandlingDetailPage terminal={terminal as Terminal} handling={unitFound as Handling} />
    }, [terminal, unitFound, t])

    const content = fetching === true ? <Loading />
        : data === undefined ? null : unit

    return <GridLayout header={header}>{content}</GridLayout>;
};

export default TerminalUnitSearch;